import { Injectable, signal, computed, inject, DOCUMENT } from '@angular/core';
import { ThemeService } from './theme.service';

interface ThemeVariables {
  [key: string]: string;
}

interface ThemeDefinition {
  name: string;
  displayName: string;
  description: string;
  light: ThemeVariables;
  dark: ThemeVariables;
}

interface ThemeJsonObject {
  name?: string;
  displayName?: string;
  description?: string;
  light?: ThemeVariables;
  dark?: ThemeVariables;
}

// All possible CSS variables collected from all themes
export const ALL_THEME_VARIABLES = [
  // Legacy variables (default, luxe, enterprise themes)
  '--primary',
  '--secondary',
  '--background',
  '--surface',
  '--primary-container',
  '--on-primary-container',
  '--on-background',
  '--on-surface',
  '--border-radius-sm',
  '--border-radius-md',
  '--border-radius-lg',
  '--border-radius-xl',
  '--shadow-sm',
  '--shadow-md',
  '--shadow-lg',
  '--shadow-xl',
  '--border-color',
  '--border-color-light',
  '--border-color-dark',

  // Modern variables (neo, zen, themes)
  '--bg',
  '--bg-elevated',
  '--bg-card',
  '--bg-card-alt',
  '--border-subtle',
  '--text-main',
  '--text-muted',
  '--accent',
  '--success',
  '--pill-bg',
  '--pill-text',
  '--cta-bg1',
  '--cta-bg2',
  '--cta-text',
  '--chip-bg',
  '--chip-border',
  '--filter-bg',
  '--filter-input-bg',
  '--seg-active-bg',
  '--seg-icon-active-bg',
  '--shell-border',
  '--shell-shadow',
  '--shell-border-radius',
  '--offer-card-border-radius',
  '--top-bar-gradient',
  '--ribbon-bg',
  '--ribbon-color',
  '--ribbon-shadow',
  '--offer-card-overlay',
  '--offer-card-overlay-blend-mode',
  '--offer-card-overlay-opacity',
  '--cta-shadow',
  '--floating-cart-shadow',
  '--font-family',
  '--filter-chip-bg-override',
  '--filter-chip-text-override',
  '--tabs-bar-bg',
];

@Injectable({
  providedIn: 'root'
})
export class ThemeBuilderService {
  private document = inject(DOCUMENT);
  private themeService = inject(ThemeService);

  // Signals for reactive state
  public isPlaygroundActive = signal<boolean>(false);
  public playgroundTheme = signal<ThemeDefinition>({
    name: 'custom',
    displayName: 'Custom Theme',
    description: 'Custom theme created in playground',
    light: {},
    dark: {}
  });

  public currentMode = signal<'light' | 'dark'>('light');

  // Computed signal for current mode variables
  public currentVariables = computed(() => {
    const theme = this.playgroundTheme();
    const mode = this.currentMode();
    return theme[mode];
  });

  constructor() {}

  /**
   * Activate playground mode and load current theme
   */
  async activatePlayground(): Promise<void> {
    this.isPlaygroundActive.set(true);

    // Load current theme from ThemeService
    const currentThemeId = this.themeService.getCurrentThemeId();
    await this.loadThemeIntoPlayground(currentThemeId);

    // Sync mode with theme service
    const isDark = this.themeService.isDarkMode();
    this.currentMode.set(isDark ? 'dark' : 'light');
  }

  /**
   * Deactivate playground mode and restore original theme
   */
  deactivatePlayground(): void {
    this.isPlaygroundActive.set(false);

    // Restore original theme
    const currentThemeId = this.themeService.getCurrentThemeId();
    const mode = this.themeService.getCurrentTheme();
    this.themeService.setTheme(currentThemeId);
  }

  /**
   * Load a theme into the playground from theme ID
   */
  async loadThemeIntoPlayground(themeId: string): Promise<void> {
    try {
      const baseUrl = this.document.baseURI || '/';
      const url = new URL(`assets/themes/${themeId}.json`, baseUrl).toString();
      const response = await fetch(url, { cache: 'no-cache' });

      if (!response.ok) {
        throw new Error(`Failed to load theme: ${themeId}`);
      }

      const themeJson = await response.json();

      // Ensure all variables exist with empty values
      const lightVars: ThemeVariables = {};
      const darkVars: ThemeVariables = {};

      ALL_THEME_VARIABLES.forEach(varName => {
        lightVars[varName] = themeJson.light?.[varName] || '';
        darkVars[varName] = themeJson.dark?.[varName] || '';
      });

      this.playgroundTheme.set({
        name: themeJson.name || themeId,
        displayName: themeJson.displayName || themeId,
        description: themeJson.description || '',
        light: lightVars,
        dark: darkVars
      });

      // Apply the loaded theme
      this.applyPlaygroundTheme();
    } catch (error) {
      console.error('Failed to load theme into playground', error);
    }
  }

  /**
   * Load a theme into the playground from an in-memory JSON object.
   * Missing variables are treated as "no override" (empty string).
   */
  loadThemeFromJsonObject(themeJson: ThemeJsonObject): void {
    const current = this.playgroundTheme();

    const lightInput = themeJson.light ?? {};
    const darkInput = themeJson.dark ?? {};

    const variableNames = new Set<string>([
      ...ALL_THEME_VARIABLES,
      ...Object.keys(lightInput),
      ...Object.keys(darkInput)
    ]);

    const lightVars: ThemeVariables = {};
    const darkVars: ThemeVariables = {};

    variableNames.forEach(varName => {
      if (!varName.startsWith('--')) return;
      lightVars[varName] = lightInput[varName] || '';
      darkVars[varName] = darkInput[varName] || '';
    });

    this.playgroundTheme.set({
      name: themeJson.name ?? current.name,
      displayName: themeJson.displayName ?? current.displayName,
      description: themeJson.description ?? current.description,
      light: lightVars,
      dark: darkVars
    });

    if (this.isPlaygroundActive()) {
      this.applyPlaygroundTheme();
    }
  }

  /**
   * Update a single CSS variable
   */
  updateVariable(varName: string, value: string): void {
    const theme = this.playgroundTheme();
    const mode = this.currentMode();

    const updatedTheme = {
      ...theme,
      [mode]: {
        ...theme[mode],
        [varName]: value
      }
    };

    this.playgroundTheme.set(updatedTheme);

    // Apply the variable in real-time
    if (this.isPlaygroundActive()) {
      this.applyPlaygroundTheme();
    }
  }

  /**
   * Switch between light and dark mode in playground
   */
  switchMode(mode: 'light' | 'dark'): void {
    this.currentMode.set(mode);

    if (this.isPlaygroundActive()) {
      this.applyPlaygroundTheme();
    }
  }

  /**
   * Apply playground theme variables to the DOM
   */
  private applyPlaygroundTheme(): void {
    const theme = this.playgroundTheme();
    const mode = this.currentMode();
    const vars = theme[mode];

    const root = this.document.documentElement;
    const body = this.document.body;

    // Apply all variables
    Object.entries(vars).forEach(([name, value]) => {
      if (name.startsWith('--') && value) {
        root.style.setProperty(name, value);
        body.style.setProperty(name, value);
      }
    });

    // Apply font-family if specified
    const fontFamily = vars['--font-family'];
    if (fontFamily) {
      body.style.fontFamily = fontFamily;
    } else {
      body.style.fontFamily = '';
    }

    // Update dark mode class
    if (mode === 'dark') {
      body.classList.add('dark-theme');
    } else {
      body.classList.remove('dark-theme');
    }

    // Update data attributes
    root.setAttribute('data-theme', mode);
    root.setAttribute('data-style', 'playground');
  }

  /**
   * Generate JSON for download
   */
  generateThemeJson(): string {
    const theme = this.playgroundTheme();

    // Remove empty values
    const cleanLight: ThemeVariables = {};
    const cleanDark: ThemeVariables = {};

    Object.entries(theme.light).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        cleanLight[key] = value;
      }
    });

    Object.entries(theme.dark).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        cleanDark[key] = value;
      }
    });

    const themeJson = {
      name: theme.name,
      displayName: theme.displayName,
      description: theme.description,
      light: cleanLight,
      dark: cleanDark
    };

    return JSON.stringify(themeJson, null, 2);
  }

  /**
   * Download theme as JSON file
   */
  downloadTheme(): void {
    const json = this.generateThemeJson();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = this.document.createElement('a');
    a.href = url;
    a.download = `${this.playgroundTheme().name}.json`;
    this.document.body.appendChild(a);
    a.click();
    this.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Update theme metadata
   */
  updateMetadata(field: 'name' | 'displayName' | 'description', value: string): void {
    const theme = this.playgroundTheme();
    this.playgroundTheme.set({
      ...theme,
      [field]: value
    });
  }
}
